import { Component, NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from '@core/guards/auth.guard';
import { S11ExchangeGuard } from '@core/guards/s11-exchange.guard';
import { HomeComponent } from '@core/components/home/home.component';
import { PageNotFoundComponent } from '@core/components/page-not-found/page-not-found.component';
import { AdfsCallbackComponent } from '@core/components/adfs-callback/adfs-callback.component';
import { MaintenanceComponent } from '@core/components/maintenance/maintenance.component';
import { PermissionNotFoundComponent } from '@shared/components/permission-not-found/permission-not-found.component';
import { AccountLockedComponent } from '@core/components/account-locked/account-locked.component';
import { DashboardContainerComponent } from '@core/components/dashboard-container/dashboard-container.component';
import { EContractGuard } from '@core/guards/econtract.guard';
import { LOA_FEATURE_CONFIG } from './modules/loa/components/loa/loa.component';
import { LocationDeniedComponent } from '@core/components/location-denied/location-denied.component';
import { ViewPreApproveComponent } from '@core/components/view-s11-documents/view-pre-approve.component';
import { ViewS11ContractComponent } from '@core/components/view-s11-documents/view-s11-contract.component';

@Component({
  selector: 'app-media-window-component',
  template: 'file is in download folder',
  styleUrls: [],
  standalone: true,
})
class MediaWindowComponent {}

const routes: Routes = [
  {
    canActivate: [AuthGuard],
    data: [
      'General',
      'IT',
      'Master Admin',
      'Admin',
      'Contract Management',
      'Dealer',
      'Checker',
      'S11 Controller',
      'S11 Commander',
    ],
    path: '',
    component: HomeComponent,
  },
  {
    path: 'login',
    component: DashboardContainerComponent,
  },
  {
    path: 'forget-password',
    component: DashboardContainerComponent,
  },
  {
    path: 'adfs-callback',
    component: AdfsCallbackComponent,
  },
  {
    path: 'window-media',
    component: MediaWindowComponent,
  },
  {
    path: 'profile-detail',
    data: {
      permission: [
        'General',
        'Master Admin',
        'Admin',
        'Auditor',
        'Contract Management',
        'Dealer',
        'Checker',
        'S11 Controller',
        'S11 Commander',
      ],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('../app/modules/profile/profile.module').then(
        (m) => m.ProfileModule,
      ),
  },
  {
    path: 'news-feed',
    data: {
      permission: [
        'General',
        'Master Admin',
        'Admin',
        'Contract Management',
        'Dealer',
        'Checker',
        'S11 Controller',
        'S11 Commander',
      ],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('../app/modules/new-feed/new-feed.module').then(
        (m) => m.NewFeedModule,
      ),
  },
  {
    path: 'announcement',
    data: {
      permission: [
        'General',
        'Master Admin',
        'Admin',
        'Contract Management',
        'Dealer',
        'Checker',
        'S11 Controller',
        'S11 Commander',
      ],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('../app/modules/announcement/announcement.module').then(
        (m) => m.AnnouncementModule,
      ),
  },
  {
    path: 'configuration',
    data: { permission: ['Master Admin', 'Admin'] },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('../app/modules/setting/setting.module').then(
        (m) => m.SettingModule,
      ),
  },
  {
    path: 'users',
    data: { permission: ['Master Admin', 'Admin', 'Auditor'] },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('../app/modules/users/users.module').then(
        (m) => m.UsersModule,
      ),
  },
  {
    path: 'manage-csr',
    data: { permission: ['Master Admin', 'Admin'] },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('../app/modules/manage-csr/manage-csr.module').then(
        (m) => m.ManageCsrModule,
      ),
  },
  {
    path: 'kpi-dashboard',
    data: { permission: ['Master Admin', 'Admin'] },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('../app/modules/dashboard/dashboard.module').then(
        (m) => m.DashboardModule,
      ),
  },
  {
    path: 'usage-dashboard',
    data: { permission: ['Admin', 'Master Admin'] },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('../app/modules/usage/usage.module').then(
        (m) => m.UsageModule,
      ),
  },
  {
    path: 'contract-package',
    data: ['Master Admin', 'Admin'],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        './modules/contract-dashboard/contract-dashboard.module'
      ).then((m) => m.ContractDashboardModule),
  },
  {
    path: 'operation-log',
    data: { permission: ['Admin', 'Master Admin', 'Auditor'] },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        '../app/modules/operation-log/operation-log.module'
      ).then((m) => m.OperationLogModule),
  },
  {
    path: 'pending-approval',
    data: {
      permission: [
        'General',
        'Admin',
        'Contract Management',
        'Dealer',
        'Checker',
        'S11 Controller',
        'S11 Commander',
      ],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        './modules/approval-request/approval-request.module'
      ).then((m) => m.ApprovalRequestModule),
  },
  {
    path: 'pending-assignment',
    data: {
      permission: ['S11 Controller'],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        './modules/approval-request/approval-request.module'
      ).then((m) => m.ApprovalRequestModule),
  },
  {
    path: 'approval',
    loadChildren: () =>
      import('../app/modules/approval/approval.module').then(
        (m) => m.ApprovalModule,
      ),
  },
  {
    path: 'upload-evidences',
    loadChildren: () =>
      import('../app/modules/evidence/evidence.module').then(
        (m) => m.EvidenceModule,
      ),
  },
  {
    path: 'loa',
    data: { permission: ['Master Admin', 'Admin'] },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('../app/modules/loa/loa.module').then(
        (m) => m.LoaModule,
      ),
  },
  {
    path: 'cc-group',
    data: { permission: ['Master Admin', 'Admin'] },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('../app/modules/cc-group/cc-group.module').then(
        (m) => m.CcGroupModule,
      ),
  },
  {
    path: 'memos',
    canActivate: [S11ExchangeGuard],
    loadChildren: () =>
      import('./modules/memos/memo.module').then((m) => m.MemoModule),
  },
  {
    path: 'contract',
    data: [
      'Contract Management',
      'Dealer',
      'Checker',
      'S11 Controller',
      'S11 Commander',
    ],
    canActivate: [EContractGuard],
    loadChildren: () =>
      import('./modules/contract/contract.module').then(
        (m) => m.ContractModule,
      ),
  },
  {
    path: 'certificate',
    data: { permission: ['Master Admin', 'Admin', 'Auditor'] },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('../app/modules/certificate/certificate.module').then(
        (m) => m.CertificateModule,
      ),
  },
  {
    path: 'reset-password',
    loadChildren: () =>
      import(
        '../app/modules/reset-password/reset-password.module'
      ).then((m) => m.ResetPasswordModule),
  },
  {
    path: 'register',
    loadChildren: () =>
      import('../app/modules/register/register.module').then(
        (m) => m.RegisterModule,
      ),
  },
  {
    path: 'set-memo-password',
    loadChildren: () =>
      import(
        '../app/modules/reset-password-via-link/reset-password-via-link.module'
      ).then((m) => m.ResetPasswordViaLinkModule),
  },
  {
    path: 'levels-hierarchy',
    data: { permission: ['Master Admin', 'Admin', 'Auditor'] },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        '../app/modules/level-of-hierarchy/level-of-hierarchy.module'
      ).then((m) => m.LevelOfHierarchyModule),
  },
  {
    path: 'budget',
    data: {
      permission: [
        'Master Admin',
        'Admin',
        'budget__can_control_budget',
        'budget__can_view_auth_budget',
      ],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('../app/modules/budget/budget.module').then(
        (m) => m.BudgetModule,
      ),
  },
  {
    path: 'budget-categories',
    data: {
      permission: [
        'Master Admin',
        'Admin',
        'budget__can_control_budget',
      ],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        '../app/modules/budget-category/budget-category.module'
      ).then((m) => m.BudgetCategoryModule),
  },
  {
    path: 'budget-loa',
    data: {
      permission: [
        'Master Admin',
        'Admin',
        'budget__can_control_budget',
      ],
    },
    canActivate: [AuthGuard],
    providers: [
      {
        provide: LOA_FEATURE_CONFIG,
        useValue: {
          budgetLoa: true,
        },
      },
    ],
    loadChildren: () =>
      import('../app/modules/loa/loa.module').then(
        (m) => m.LoaModule,
      ),
  },
  {
    path: 'budget-master',
    data: {
      permission: [
        'Master Admin',
        'Admin',
        'budget__can_control_budget',
      ],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        '../app/modules/budget-master/budget-master.module'
      ).then((m) => m.BudgetMasterModule),
  },
  {
    path: 'departments',
    data: { permission: ['Master Admin', 'Admin', 'Auditor'] },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('../app/modules/department/department.module').then(
        (m) => m.DepartmentModule,
      ),
  },
  {
    path: 'budget-dashboard',
    data: {
      permission: [
        'Master Admin',
        'Admin',
        'budget__can_control_budget',
        'budget__can_view_auth_budget',
      ],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        '../app/modules/budget-dashboard/budget-dashboard.module'
      ).then((m) => m.BudgetDashboardModule),
  },
  { path: 'website-maintenance', component: MaintenanceComponent },
  { path: 'page-not-found', component: PageNotFoundComponent },
  { path: 'account-locked', component: AccountLockedComponent },
  {
    path: 'location-denied/:jwtToken',
    canActivate: [AuthGuard],
    component: LocationDeniedComponent,
  },
  {
    path: 'permission-not-found',
    component: PermissionNotFoundComponent,
  },
  {
    path: 'folders',
    loadChildren: () =>
      import('./modules/folder/folder.module').then(
        (m) => m.FolderModule,
      ),
  },
  {
    path: 'memo-types',
    data: { permission: ['Master Admin', 'Admin'] },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('../app/modules/memo-type/memo-type.module').then(
        (m) => m.MemoTypeModule,
      ),
  },
  {
    path: 's11-documents',
    data: {
      permission: [
        'General',
        'Master Admin',
        'Admin',
        'Auditor',
        'Contract Management',
        'Dealer',
        'Checker',
        'S11 Controller',
        'S11 Commander',
      ],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./modules/s11-e-signature/s11-e-signature.module').then(
        (m) => m.S11EsignatureModule,
      ),
  },
  {
    path: 'upload-s11-esign-evidences',
    loadChildren: () =>
      import(
        './modules/evidences-pre-approve/evidences-pre-approve.module'
      ).then((m) => m.EvidencesPreApproveModule),
  },
  {
    path: 'e-document',
    data: { permission: ['Master Admin', 'Admin'] },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('../app/modules/e-document/e-document.module').then(
        (m) => m.EDocumentModule,
      ),
  },
  {
    path: 'other-reference',
    data: { permission: ['Master Admin', 'Admin'] },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./modules/other-reference/other-reference.module').then(
        (m) => m.OtherReferenceModule,
      ),
  },
  {
    path: 'view-pre-approve/:id',
    component: ViewPreApproveComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'view-s11-contract/:id',
    canActivate: [AuthGuard],
    component: ViewS11ContractComponent,
  },
  { path: '**', component: PageNotFoundComponent },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
